import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateFormData, setExpectedCurrencyForOrigin } from '../../../redux/slices/listingSlice'
import { determineExpectedCurrency } from '../../../utils/determineOriginCurrency'
import FormInput from '../../ui/FormInput'
import FormCheckbox from '../../ui/FormCheckbox'
import Dropdown from '../../ui/Dropdown'
import AddressList from '../../../components/address/AddressList'
import { AddressDropdown } from '../../../components/address'
import AddressForm from '../../../components/address/AddressForm'
import ServiceRadiusMap from '../../map/ServiceRadiusMap'
import ParcelInfoInput from '../../shipping/ParcelInfoInput'
import { useGetAddressesQuery } from '../../../redux/apis'
import { showError, showSuccess } from '../../../utils/toast'

const AvailabilitySection = ({ isLocalService = false }) => {
  const dispatch = useDispatch()
  const formData = useSelector(state => state.listing.form.formData)
  const fieldErrors = useSelector(state => state.listing.form.fieldErrors)
  const currentUser = useSelector(state => state.auth.user)

  const { data: addresses = [] } = useGetAddressesQuery()
  
  // Local state for stock input
  const [stockValue, setStockValue] = useState('')
  // Local state for service radius
  const [serviceRadius, setServiceRadius] = useState(
    formData.serviceRadius || 10
  )
  
  // Check if product is virtual
  const isVirtual = formData.productType === 'virtual'
  
  useEffect(() => {
    if (isVirtual) {
      dispatch(
        updateFormData({
          openToShipping: false,
          openToPickUp: false,
          stock: 999,
          forSell: true,
        })
      )
    }
  }, [dispatch, isVirtual])

  // Central useEffect to determine and set expected currency
  useEffect(() => {
    const expectedCurrency = determineExpectedCurrency(formData, addresses, currentUser)
    dispatch(setExpectedCurrencyForOrigin(expectedCurrency))
  }, [
    formData.openToShipping,
    formData.selectedShippingAddressId,
    formData.openToPickUp,
    formData.selectedAddressId,
    formData.productType,
    addresses,
    currentUser,
    dispatch
  ])
  
  // Initialize stock value from Redux state
  useEffect(() => {
    if (!isVirtual) {
      setStockValue(formData.stock !== undefined ? String(formData.stock) : '')
    }
  }, [isVirtual, formData.stock])

  // Initialize service radius from Redux state
  useEffect(() => {
    if (isLocalService) {
      setServiceRadius(formData.serviceRadius || 10)
    }
  }, [isLocalService, formData.serviceRadius])

  // Sync local stock value with Redux state changes
  useEffect(() => {
    if (
      !isVirtual &&
      (!document.activeElement || document.activeElement.name !== 'stock')
    ) {
      if (formData.stock !== undefined) {
        setStockValue(formData.stock === 0 ? '' : String(formData.stock))
      }
    }
  }, [formData.stock, isVirtual])
  
  // Handler for the stock input field
  const handleStockChange = e => {
    const value = e.target.value
    
    // Only allow integers (whole numbers)
    if (value === '' || /^[0-9]*$/.test(value)) {
      setStockValue(value)
      
      // Update redux with the value
      const numValue = value === '' ? '' : Number(value)
      dispatch(
        updateFormData({
        stock: numValue
        })
      )
    }
  }

  // Handler for the service radius slider
  const handleRadiusChange = e => {
    const value = Number(e.target.value)
    setServiceRadius(value)

    dispatch(
      updateFormData({
      serviceRadius: value
      })
    )
  }

  const handleToggle = field => {
    const newFieldValue = !formData[field]
    const updateData = {
      [field]: newFieldValue
    }

    if (isVirtual) {
      updateData.openToShipping = false
      updateData.openToPickUp = false
    } else {
      if (field === 'openToShipping') {
        if (!newFieldValue) { // Turning OFF shipping
          updateData.shippingAddress = null
          updateData.selectedShippingAddressId = ''
          if (formData.useShippingForPickup) {
            updateData.pickUpAddress = null
            updateData.selectedAddressId = ''
            updateData.useShippingForPickup = false
          }
        }
      } else if (field === 'openToPickUp') {
        if (!newFieldValue) { // Turning OFF pickup
          updateData.pickUpAddress = null
          updateData.selectedAddressId = ''
          if (formData.useShippingForPickup) {
            // This implies the link is broken, no need to clear shipping address
            updateData.useShippingForPickup = false
          }
        }
      }
      // Ensure at least one delivery option is selected if not virtual
      if (field === 'openToPickUp' && !newFieldValue && !formData.openToShipping) {
        updateData.openToShipping = true
      } else if (field === 'openToShipping' && !newFieldValue && !formData.openToPickUp) {
        updateData.openToPickUp = true
      }
    }

    dispatch(updateFormData(updateData))
  }

  // Handle parcel info changes
  const handleParcelInfoChange = parcelInfo => {
    dispatch(
      updateFormData({
        parcelInfo
      })
    )
  }

  // Handle toggle for using shipping address as pickup address
  const handleUseShippingForPickup = (e, data) => {
    const newValue = data ? data.checked : !formData.useShippingForPickup
    const currentShippingAddress = addresses.find(addr => addr.id === formData.selectedShippingAddressId) || formData.shippingAddress

    if (newValue) {
      if (!currentShippingAddress) {
        showError('Please select a shipping address first')
        return
      }
      dispatch(
        updateFormData({
          useShippingForPickup: true,
          pickUpAddress: currentShippingAddress,
          selectedAddressId: formData.selectedShippingAddressId
        })
      )
    } else {
      dispatch(
        updateFormData({
          useShippingForPickup: false,
          pickUpAddress: null,
          selectedAddressId: ''
        })
      )
    }
  }

  // Handle shipping address selection
  const handleShippingAddressSelect = (e) => {
    const selectedId = e.target.value
    let shippingAddress = null

    if (selectedId) {
      shippingAddress = addresses.find(addr => addr.id === selectedId)
      if (shippingAddress) {
        const updates = {
          shippingAddress: shippingAddress,
          selectedShippingAddressId: selectedId
        }
        if (formData.useShippingForPickup) {
          updates.pickUpAddress = shippingAddress
          updates.selectedAddressId = selectedId
        }
        dispatch(updateFormData(updates))
      }
    } else {
      // Shipping address deselected
      const updates = {
        shippingAddress: null,
        selectedShippingAddressId: ''
      }
      if (formData.useShippingForPickup) {
        // If it was also the pickup address, clear that too and toggle off useShippingForPickup
        updates.pickUpAddress = null
        updates.selectedAddressId = ''
        updates.useShippingForPickup = false // Toggle this off as the source shipping address is gone
      }
      dispatch(updateFormData(updates))
    }
  }

  // Function to convert parcel dimensions between unit systems
  const convertParcelDimensions = (parcelInfo, targetRegion) => {
    // If no parcel info or missing dimensions, return as is
    if (!parcelInfo || 
        parcelInfo.length === undefined || 
        parcelInfo.width === undefined || 
        parcelInfo.height === undefined || 
        parcelInfo.weight === undefined) {
      return parcelInfo;
    }
    
    // Check if conversion is needed based on units
    if (parcelInfo.units) {
      const currentUnitSystem = parcelInfo.units.distance;
      const targetUnitSystem = targetRegion === 'US' ? 'in' : 'cm';
      
      // If units already match target region, no conversion needed
      if (currentUnitSystem === targetUnitSystem) {
        console.log('No parcel dimension conversion needed, units already match:', currentUnitSystem);
        return parcelInfo;
      }
    }
    
    // Convert from US to international (inches/lb to cm/kg)
    if (targetRegion !== 'US') {
      return {
        ...parcelInfo,
        length: (parseFloat(parcelInfo.length) * 2.54).toFixed(1),
        width: (parseFloat(parcelInfo.width) * 2.54).toFixed(1), 
        height: (parseFloat(parcelInfo.height) * 2.54).toFixed(1),
        weight: (parseFloat(parcelInfo.weight) * 0.453592).toFixed(2),
        units: { distance: 'cm', mass: 'kg' }
      };
    }
    
    // Convert from international to US (cm/kg to inches/lb)
    if (targetRegion === 'US') {
      return {
        ...parcelInfo,
        length: (parseFloat(parcelInfo.length) / 2.54).toFixed(1),
        width: (parseFloat(parcelInfo.width) / 2.54).toFixed(1),
        height: (parseFloat(parcelInfo.height) / 2.54).toFixed(1),
        weight: (parseFloat(parcelInfo.weight) / 0.453592).toFixed(2),
        units: { distance: 'in', mass: 'lb' }
      };
    }
    
    return parcelInfo;
  };

  // Handle pickup address selection
  const handlePickupAddressSelect = (e) => {
    const selectedId = e.target.value
    let pickUpAddress = null // This variable is for the potential new selection

    if (selectedId) {
      pickUpAddress = addresses.find(addr => addr.id === selectedId)
      if (pickUpAddress) {
        const updates = {
          pickUpAddress: pickUpAddress,
          selectedAddressId: selectedId
        }
        // If user selects a pickup address, and it's different from shipping addr, useShippingForPickup should be false
        if (formData.shippingAddress && formData.shippingAddress.id !== pickUpAddress.id) {
            updates.useShippingForPickup = false;
        } else if (!formData.shippingAddress) {
            updates.useShippingForPickup = false; // if no shipping address, can't be using it for pickup
        } 
        // If the selected pickup IS the shipping address, and useShippingForPickup was false, set to true.
        // This situation implies the user explicitly chose the address that also happens to be the shipping address from the pickup dropdown.
        else if (formData.shippingAddress && formData.shippingAddress.id === pickUpAddress.id && !formData.useShippingForPickup) {
            updates.useShippingForPickup = true;
        }

        dispatch(updateFormData(updates))
      }
    } else {
      // Pickup address deselected (dropdown set to empty/placeholder)
      const updates = {
        pickUpAddress: null,
        selectedAddressId: ''
      }
      // If the deselected pickup address was the one linked via useShippingForPickup.
      // Check current formData.pickUpAddress to see if it matches shippingAddress before nullifying it.
      if (formData.useShippingForPickup && formData.pickUpAddress && formData.pickUpAddress.id === formData.shippingAddress?.id) {
        updates.useShippingForPickup = false; 
      }
      dispatch(updateFormData(updates))
    }
  }

  const renderAddressDetails = (address) => {
    if (!address) return null;
    
    return (
      <div className="mt-2 bg-white p-3 rounded-md border border-gray-200">
        <p className="text-sm font-medium">{address.name || 'Address'}</p>
        <p className="text-sm text-gray-600">{address.street}{address.apartment ? `, ${address.apartment}` : ''}</p>
        <p className="text-sm text-gray-600">
          {address.city}, {address.state} {address.postalCode}
        </p>
        <p className="text-sm text-gray-600">{address.country || 'US'}</p>
      </div>
    );
  };

  const handlePackageStepSubmit = () => {
    return new Promise((resolve, reject) => {
      try {
        if (!formData.shippingAddress) {
          // Show error if no shipping address is selected
          showError('Please select a shipping address first')
          return
        }
        // ... existing code ...
      } catch (error) {
        reject(error)
      }
    })
  }

  return (
    <div className='space-y-6  mx-auto bg-white p-6 rounded-lg'>
      <h2 className='text-xl font-semibold text-gray-800 mb-4'>
        {isLocalService ? 'Service Area' : 'Availability'}
      </h2>
      
      {/* First Row: Stock and Options - Hide for virtual products and local services */}
      {!isVirtual && !isLocalService && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* Left: Stock */}
          <div>
            <FormInput
              label='Stock'
              name='stock'
              type='number'
              value={stockValue}
              onChange={handleStockChange}
              error={fieldErrors?.stock}
              required
              placeholder='0'
              min='0'
              fluid={false}
            />
          </div>

          {/* Right: Delivery Options */}
          <div>
            <label className='block text-label font-medium text-gray-700 mb-2'>
              Delivery Options <span className='text-red-500 ml-1'>*</span>
            </label>
            <div
              className={`space-y-3 ${
                fieldErrors?.deliveryOptions
                  ? 'p-3 border border-red-300 rounded-md bg-red-50'
                  : ''
              }`}
            >
              <div className='flex flex-col space-y-2'>
                <FormCheckbox
                  label='Shipping'
                  name='openToShipping'
                  checked={formData?.openToShipping || false}
                  onChange={() => handleToggle('openToShipping')}
                />
                
                <FormCheckbox
                  label='Local Pickup'
                  name='openToPickUp'
                  checked={formData?.openToPickUp || false}
                  onChange={() => handleToggle('openToPickUp')}
                />
              </div>
              
              {fieldErrors?.deliveryOptions && (
                <p className='text-sm text-red-600'>
                  {fieldErrors.deliveryOptions}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Shipping Section - Show when shipping is enabled */}
      {!isVirtual && !isLocalService && formData?.openToShipping && (
        <div className='bg-gray-50 rounded-lg p-4'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>
            Shipment Information <span className='text-red-500'>*</span>
          </h3>
          
          {/* Shipping Address */}
          <div className='mb-6'>
            <h4 className='text-base font-medium text-gray-900 mb-3'>
              Shipping Address <span className='text-red-500'>*</span>
            </h4>
            
            {/* Address Selection and Form */}
            {formData?.selectedShippingAddressId === 'new' ? (
              <div className='mt-2'>
                <AddressForm
                  isPickupAddress={false}
                  showMap={false}
                  isInline={true}
                  onSuccess={(savedAddress) => {
                    dispatch(updateFormData({
                      selectedShippingAddressId: savedAddress.id,
                      shippingAddress: savedAddress
                    }));
                    
                    // Also update pickup address if "Same as shipping" is checked
                    if (formData.useShippingForPickup) {
                      dispatch(updateFormData({
                        pickUpAddress: savedAddress,
                        selectedAddressId: savedAddress.id
                      }));
                    }
                  }}
                />
              </div>
            ) : (
              <AddressDropdown
                label=""
                name="selectedShippingAddressId"
                value={formData?.selectedShippingAddressId || ''}
                onChange={handleShippingAddressSelect}
                error={fieldErrors?.shippingAddress}
                placeholder="Select shipping address"
                showAddNew={true}
                required
              />
            )}
          </div>
          
          {/* Package Information */}
          <div className='mt-6'>
            <h4 className='text-base font-medium text-gray-900 mb-3'>
              Package Information <span className='text-red-500'>*</span>
            </h4>
            <ParcelInfoInput
              parcelInfo={formData.parcelInfo || {
                length: '',
                width: '',
                height: '',
                weight: '',
                units: formData.shippingAddress?.country === 'US' ? 
                  { distance: 'in', mass: 'lb' } : 
                  { distance: 'cm', mass: 'kg' }
              }}
              onChange={handleParcelInfoChange}
              errors={fieldErrors?.parcelInfo || {}}
              region={formData.shippingAddress?.country === 'US' || 
                     (formData.useShippingForPickup && formData.shippingAddress?.country === 'US') ? 
                     'US' : 'international'}
            />
          </div>
        </div>
      )}

      {/* Service Radius for Local Services */}
      {isLocalService && (
        <div className='bg-gray-50 p-4 rounded-lg mb-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-3'>
            Service Radius
          </h3>
          <p className='text-sm text-gray-600 mb-4'>
            Set the maximum distance you're willing to travel for providing your
            service.
          </p>

          <div className='max-w-md'>
            <div className='flex items-center justify-between mb-2'>
              <span className='text-sm text-gray-500'>1 mile</span>
              <span className='text-sm font-medium'>{serviceRadius} miles</span>
              <span className='text-sm text-gray-500'>50 miles</span>
            </div>
            
            <input
              type='range'
              min='1'
              max='50'
              value={serviceRadius}
              onChange={handleRadiusChange}
              className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer'
            />
          </div>
        </div>
      )}

      {/* Pickup Address Section - Full Width - For both regular products with pickup and local services */}
      {(!isVirtual && formData?.openToPickUp) || isLocalService ? (
        <div className='mt-4 p-4 bg-gray-50 rounded-lg'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>
            {isLocalService ? 'Service Location' : 'Pickup Address'} <span className='text-red-500 ml-1'>*</span>
          </h3>
          <div className='max-w-2xl'>
            <p className='text-sm text-gray-600 mb-4'>
              {isLocalService 
                ? 'This is your primary service location. The exact address will only be shared with approved clients.'
                : 'This location will only be shared with approved buyers/renters when they book a pickup.'}
            </p>

            {/* "Same as shipping address" checkbox - Only show when shipping is enabled and shipping address is set */}
            {!isLocalService && formData?.openToShipping && formData?.shippingAddress && (
              <div className='mb-4'>
                <FormCheckbox
                  label='Same as shipping address'
                  name='useShippingForPickup'
                  checked={formData.useShippingForPickup || false}
                  onChange={handleUseShippingForPickup}
                />
                    </div>
                  )}
                  
            {/* Pickup Address Selection - Always show, but disable when "Same as shipping" is checked */}
            {formData.selectedAddressId === 'new' && !formData.useShippingForPickup ? (
              <div className='mt-2'>
                    <AddressForm
                      isPickupAddress={true}
                      showMap={true}
                      isInline={true}
                      onSuccess={(savedAddress) => {
                        dispatch(updateFormData({
                          selectedAddressId: savedAddress.id,
                      pickUpAddress: savedAddress,
                      useShippingForPickup: false
                        }));
                      }}
                />
              </div>
            ) : (
              <>
                {/* Always show the dropdown, but disable it if using shipping address */}
                <AddressDropdown
                  label=""
                  name="selectedAddressId"
                  value={formData.useShippingForPickup ? formData.selectedShippingAddressId : (formData?.selectedAddressId || '')}
                  onChange={handlePickupAddressSelect}
                  error={fieldErrors?.pickUpAddress}
                  placeholder="Select pickup address"
                  showAddNew={!formData.useShippingForPickup}
                  required
                  // disabled={formData.useShippingForPickup}
                />
                
                {/* {formData.useShippingForPickup && (
                  <div className='mt-2 text-sm text-gray-500 italic'>
                    Using the same address as shipping
                  </div>
                )} */}
                
                {/* For local services, show the service radius map */}
                {isLocalService && formData.pickUpAddress && formData.pickUpAddress.coordinates && (
                  <div className='mt-4 border border-gray-200 rounded-lg overflow-hidden'>
                    <ServiceRadiusMap 
                      address={formData.pickUpAddress} 
                      radius={serviceRadius}
                      height="300px"
                    />
                    <div className='p-3 bg-white text-sm text-gray-500 text-center border-t'>
                      Your service area covers a {serviceRadius} mile radius from this location
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default AvailabilitySection
